import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import {
  useFetchTicketProviderTicketCommentsQuery,
} from 'api/ticketProviderTickets'

import {
  useAnchorScroll, useHashIdByKey,
  useLocationQueryParams, useSetSearchParamsWithQueryString,
} from 'utils/hooks'
import { useExpandablePageSection } from 'components/Shared/ExpandablePageSection/hooks'

import { capitalizeFirstLetter, stringifyQuery } from 'utils/strings'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import ExpandablePageSection from 'components/Shared/ExpandablePageSection/Index'
import FilterPanel from 'components/Shared/Skeleton/FilterPanel/Index'
import FilterPanelTitle from 'components/Shared/Skeleton/FilterPanel/FilterPanelTitle'
import UnwrappableSection from 'components/Shared/Skeleton/FilterPanel/UnwrappableSection'
import FilterWithDropdown from 'components/Shared/FilterWithDropdown/Index'
import FilterPanelSearch from 'components/Shared/Skeleton/FilterPanel/FilterPanelSearch'
import ClearFiltersIcon from 'components/Shared/ClearFiltersIcon/Index'
import SortDropdown from 'components/Shared/SortDropdown/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import CommentsTable from './CommentsTable'
import Pagination from 'components/Shared/Pagination/Index'

const StyledFilterPanel = styled(FilterPanel)`
  padding-top: 18px;
`

const FilterAndTable = styled.div`
  position: relative;
`

const TicketComments = ({ ticket }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    isExpanded,
    toggleExpanded,
  } = useExpandablePageSection()

  const [locationQueryParams, location] = useLocationQueryParams()
  const setQueryParam = useSetSearchParamsWithQueryString()

  const activePage = parseInt(locationQueryParams.comments_page) || 1
  const searchValue = locationQueryParams.comments_search || ''
  const partnersFilter = locationQueryParams.comments_partners_filter || []
  const authorsFilter = locationQueryParams.comments_authors_filter || []
  const sortDir = locationQueryParams.comments_sort_dir || 'desc'
  const queryHashCommentId = useHashIdByKey('comments')

  const noTicket = Boolean(!ticket.id || !ticket.canShowComments)

  const { data, isFetching } = useFetchTicketProviderTicketCommentsQuery({
    ticketProviderId: ticket.ticketProviderId,
    ticketId: ticket.externalId,
    page: activePage,
    partners: partnersFilter,
    authors: authorsFilter,
    searchValue: searchValue,
    sortDir: sortDir,
    commentId: queryHashCommentId,
  },
  {
    skip: noTicket,
  })

  const comments = data?.comments || []
  const commentsCount = data?.meta?.count || 0
  const totalPages = data?.meta?.totalPages || 1
  const partnersList = data?.meta?.partnerList || []
  const authorsList = data?.meta?.authorList || []
  const commentsPage = queryHashCommentId && data?.meta?.page
  const hasRichText = data?.meta?.useRichText

  useEffect(() => {
    if (isFetching || !commentsPage) {
      return
    }

    navigate(`?${stringifyQuery({ comments_page: commentsPage })}#comments-${queryHashCommentId}`)
  }, [isFetching, commentsPage, activePage, navigate, queryHashCommentId])

  const partnersOptions = partnersList.map(partner => (
    {
      value: partner,
      label: capitalizeFirstLetter(partner),
    }
  ))

  const authorsOptions = authorsList.map(author => (
    {
      value: author,
      label: author,
    }
  ))

  // if anchor is set in URL,
  // scroll to the comments section after ticket is loaded OR
  // scroll to the specific comment after ticket and its comments are loaded
  const anchorElementId = useAnchorScroll({
    skip: Boolean(noTicket || isFetching),
  })

  if (noTicket) {
    return null
  }

  const getDefaultUrlParams = () => {
    const params = {
      comments_search: searchValue,
      comments_partners_filter: partnersFilter,
      comments_authors_filter: authorsFilter,
      comments_sort_dir: sortDir,
    }

    return params
  }

  const updateURL = (params) => {
    const newParams = {
      ...locationQueryParams,
      ...getDefaultUrlParams(),
      ...params,
      comments_page: 1,
    }

    setQueryParam(newParams)
  }

  const onSearch = (newSearchValue) => {
    updateURL({
      comments_search: newSearchValue,
    })
  }

  const onFilterChange = (value, filterName) => {
    updateURL({
      [filterName]: value,
    })
  }

  const getClearFiltersUrl = () => {
    const queryParams = { ...locationQueryParams }
    delete queryParams['comments_search']
    delete queryParams['comments_partners_filter']
    delete queryParams['comments_authors_filter']
    delete queryParams['comments_sort_dir']

    return `${location.pathname}?${stringifyQuery(queryParams)}`
  }

  const onSortFieldChange = ({ sortDir }) => {
    updateURL({
      comments_sort_dir: sortDir,
    })
  }

  const isAnyActiveFilters = partnersFilter.length > 0 || authorsFilter.length > 0
  const isAnyActiveFiltersOrSearch = searchValue || isAnyActiveFilters
  const showFilterPanel = commentsCount > 0 || isAnyActiveFiltersOrSearch

  return (
    <ExpandablePageSection
      id='comments'
      title={t('ticketProviderTicketPage.comments')}
      badgeText={commentsCount}
      isExpanded={isExpanded}
      onToggleExpanded={toggleExpanded}
      dataTest='ticket-comments-section'
    >
      <FilterAndTable>
        <LoadingIndicator loading={isFetching} small />

        {showFilterPanel && (
          <StyledFilterPanel dataTest='ticket-comments-filter-panel'>
            <FilterPanelTitle
              withoutIcon
              title={t('ticketProviderTicketPage.filteredBy')}
            />
            <FilterWithDropdown
              onChange={(v) => onFilterChange(v, 'comments_partners_filter')}
              value={partnersFilter}
              options={partnersOptions}
              iconName='icon-partner'
              placeholder={t('ticketProviderTicketPage.commentsFilter.partnersFilterPlaceholder')}
              isMulti
              noAlphabetOrder
              withBadges
              dataTest='partners-filter'
            />
            <FilterWithDropdown
              onChange={(v) => onFilterChange(v, 'comments_authors_filter')}
              value={authorsFilter}
              options={authorsOptions}
              iconName='icon-user-16'
              placeholder={t('ticketProviderTicketPage.commentsFilter.authorsFilterPlaceholder')}
              isMulti
              noAlphabetOrder
              dataTest='authors-filter'
            />

            <UnwrappableSection>
              <FilterPanelSearch
                key={searchValue}
                initialValue={searchValue}
                onChange={onSearch}
                placeholder={t('ticketProviderTicketPage.commentsFilter.searchPlaceholder')}
                buttonTooltip={t('ticketProviderTicketPage.commentsFilter.filterPanelSearchTooltip')}
                tooltipId='tooltip-comments-filter-panel'
              />
              {isAnyActiveFiltersOrSearch && (
                <ClearFiltersIcon
                  clearUrl={getClearFiltersUrl()}
                  tooltipText={t('ticketsPage.clearFiltersLink')}
                />
              )}
              <div />
            </UnwrappableSection>

            <SortDropdown
              title={t('common.sortedBy')}
              onChange={onSortFieldChange}
              value={{ sortField: 'CREATED', sortDir: sortDir }}
              options={[{
                label: t('ticketProviderTicketPage.commentsFilter.sortFieldLabel'),
                value: 'CREATED',
              }]}
              defaultSortField='CREATED'
              defaultSortDir='desc'
              clearActionTooltip={t('ticketProviderTicketPage.commentsFilter.filterPanelClearSortingTooltip')}
            />
            <Tooltip id='tooltip-comments-filter-panel' />
          </StyledFilterPanel>
        )}
        <CommentsTable
          ticket={ticket}
          comments={comments}
          anchorElementId={anchorElementId}
          searchValue={searchValue}
          hasRichText={hasRichText}
        />
        <Pagination
          activePage={activePage}
          totalPages={totalPages}
          disabled={isFetching}
          pageParamName='comments_page'
        />
      </FilterAndTable>
    </ExpandablePageSection>
  )
}

TicketComments.propTypes = {
  ticket: PropTypes.object.isRequired,
}

export default TicketComments
